import { useCallback, useState } from "react";

import { useEmployeeProfilesTrackingService } from "app/pages/sales-intelligence/modules/employee-profiles/hooks/useEmployeeProfilesTrackingService";
import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

type UseEmployeeProfilesEditingParams = {
    canManageShared: boolean;
};

export const useEmployeeProfilesEditing = ({ canManageShared }: UseEmployeeProfilesEditingParams) => {
    const employeeProfilesTracking = useEmployeeProfilesTrackingService();

    const [sharedProfileToEdit, setSharedProfileToEdit] = useState<ProfileDto | undefined>(undefined);
    const [isProfileModalOpen, setProfileModalOpen] = useState(false);
    const [profileForEdit, setProfileForEdit] = useState<ProfileDto | null>(null);
    const [isCreatingShared, setCreatingShared] = useState(false);

    const openProfileModal = useCallback((profile: ProfileDto | null, isSharedWithAccount: boolean) => {
        setProfileForEdit(profile);
        setCreatingShared(isSharedWithAccount);
        setProfileModalOpen(true);
    }, []);

    const handleNewEmployeeProfile = useCallback(
        (isSharedWithAccount: boolean) => {
            employeeProfilesTracking.trackNewEmployeeProfile();
            // Only Admin/Champion get the private-vs-shared menu, so only they report a visibility pick.
            if (canManageShared) {
                employeeProfilesTracking.trackVisibilityChange(isSharedWithAccount);
            }
            openProfileModal(null, isSharedWithAccount);
        },
        [canManageShared, employeeProfilesTracking, openProfileModal],
    );

    const handleEditEmployeeProfile = useCallback(
        (profile: ProfileDto) => {
            if (isSharedEmployeeProfile(profile)) {
                setSharedProfileToEdit(profile);
                return;
            }
            employeeProfilesTracking.trackEditEmployeeProfile(false);
            openProfileModal(profile, false);
        },
        [employeeProfilesTracking, openProfileModal],
    );

    const handleConfirmSharedEdit = useCallback(() => {
        if (!sharedProfileToEdit) {
            return;
        }
        employeeProfilesTracking.trackEditEmployeeProfile(true);
        openProfileModal(sharedProfileToEdit, true);
        setSharedProfileToEdit(undefined);
    }, [employeeProfilesTracking, openProfileModal, sharedProfileToEdit]);

    const handleCancelSharedEdit = useCallback(() => {
        setSharedProfileToEdit(undefined);
    }, []);

    const handleCloseProfileModal = useCallback(() => {
        setProfileModalOpen(false);
        setProfileForEdit(null);
    }, []);

    return {
        isSharedEditModalOpen: sharedProfileToEdit !== undefined,
        isProfileModalOpen,
        isCreatingShared,
        profileForEdit,
        handleNewEmployeeProfile,
        handleEditEmployeeProfile,
        handleConfirmSharedEdit,
        handleCancelSharedEdit,
        handleCloseProfileModal,
    };
};
